import React, { useState } from 'react';
import { Search, Plus, ShieldCheck, Mail, Phone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { PageHeader, Card, Table, Tr, Td, SimpleBadge, Avatar, FilterChips } from './shared';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STAFF_INIT = [
  { id: 's1', name: 'Administrador', email: 'admin@delivery7tocoa.com', role: 'Super Admin', status: 'active' },
  { id: 's2', name: 'Karen Reyes', email: 'karen@delivery7tocoa.com', role: 'Operaciones', status: 'active' },
  { id: 's3', name: 'Mario Flores', email: 'mario@delivery7tocoa.com', role: 'Pagos', status: 'inactive' },
];

const AdminUsers = () => {
  const { customers, updateCustomer } = useApp();
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [staff, setStaff] = useState(STAFF_INIT);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'Operaciones' });

  const list = customers.filter((c) => (filter === 'all' || c.status === filter) && (!q || c.name.toLowerCase().includes(q.toLowerCase()) || c.phone.includes(q)));

  return (
    <div>
      <PageHeader title="Usuarios" subtitle="Clientes registrados y personal administrativo" />
      <Tabs defaultValue="clientes">
        <TabsList className="bg-white border border-gray-200 h-11 p-1 rounded-xl mb-4">
          <TabsTrigger value="clientes" className="rounded-lg px-5 data-[state=active]:bg-[#EE3B36] data-[state=active]:text-white">Clientes</TabsTrigger>
          <TabsTrigger value="staff" className="rounded-lg px-5 data-[state=active]:bg-[#EE3B36] data-[state=active]:text-white">Personal / Staff</TabsTrigger>
        </TabsList>
        <TabsContent value="clientes">
          <Card>
            <div className="p-4 flex flex-col lg:flex-row lg:items-center gap-3 border-b border-gray-100">
              <FilterChips value={filter} onChange={setFilter} options={[{ value: 'all', label: 'Todos', count: customers.length }, { value: 'active', label: 'Activos' }, { value: 'inactive', label: 'Inactivos' }]} />
              <div className="lg:ml-auto relative"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente..." className="h-9 pl-9 pr-3 rounded-lg border border-gray-200 text-sm w-[220px] focus:outline-none focus:border-[#EE3B36]" /></div>
            </div>
            <div className="p-3">
              <Table columns={['Cliente', 'Teléfono', 'Correo', 'Pedidos', 'Registro', 'Estado', 'Activo']} rows={list}
                renderRow={(c) => (
                  <Tr key={c.id} data-testid={`user-row-${c.id}`}>
                    <Td><span className="flex items-center gap-2.5"><Avatar src={c.avatar} name={c.name} size={34} /><span className="font-semibold">{c.name}</span></span></Td>
                    <Td className="text-gray-600">{c.phone}</Td>
                    <Td className="text-gray-600">{c.email}</Td>
                    <Td className="font-semibold">{c.orders}</Td>
                    <Td className="text-gray-600">{c.createdAt}</Td>
                    <Td><SimpleBadge tone={c.status === 'active' ? 'green' : 'gray'}>{c.status === 'active' ? 'Activo' : 'Inactivo'}</SimpleBadge></Td>
                    <Td><Switch checked={c.status === 'active'} onCheckedChange={(v) => updateCustomer(c.id, { status: v ? 'active' : 'inactive' }).then(() => toast.success(v ? 'Usuario activado' : 'Usuario desactivado'))} /></Td>
                  </Tr>
                )} />
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="staff">
          <Card title="Personal administrativo" action={<Button size="sm" className="bg-[#EE3B36] hover:bg-[#D93129] gap-1.5" onClick={() => setOpen(true)}><Plus size={14} /> Agregar</Button>}>
            <div className="p-3">
              <Table columns={['Nombre', 'Correo', 'Rol', 'Estado', '']} rows={staff}
                renderRow={(s) => (
                  <Tr key={s.id}>
                    <Td><span className="flex items-center gap-2.5"><Avatar name={s.name} size={34} /><span className="font-semibold">{s.name}</span></span></Td>
                    <Td className="text-gray-600">{s.email}</Td>
                    <Td><span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#2F80ED]" /> {s.role}</span></Td>
                    <Td><SimpleBadge tone={s.status === 'active' ? 'green' : 'gray'}>{s.status === 'active' ? 'Activo' : 'Inactivo'}</SimpleBadge></Td>
                    <Td><button onClick={() => { setStaff((st) => st.filter((x) => x.id !== s.id)); toast('Usuario removido'); }} className="h-8 w-8 rounded-lg border border-gray-200 text-gray-500 hover:text-red-600 hover:bg-red-50 flex items-center justify-center" disabled={s.role === 'Super Admin'}><Trash2 size={14} /></button></Td>
                  </Tr>
                )} />
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-extrabold text-xl">Nuevo usuario administrativo</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Correo</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-gray-600">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Operaciones">Operaciones</SelectItem><SelectItem value="Pagos">Pagos</SelectItem><SelectItem value="Soporte">Soporte</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter className="mt-4"><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button className="bg-[#EE3B36] hover:bg-[#D93129]" onClick={() => { if (!form.name || !form.email) return toast.error('Completa los campos'); setStaff((s) => [...s, { ...form, id: `s${Date.now()}`, status: 'active' }]); setOpen(false); toast.success('Usuario creado'); }}>Crear</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
